import type { LocalDate, MetroidPaymentMethodResultType } from '@zg-rentals/rental-manager-env';
import { numberUtils, useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { BETA_COOKIE, PageWrapper } from '@zg-rentals/rental-revenue-components';
import { GuestPayFooter } from './GuestPayFooter';
import {
  Box,
  FieldSet,
  FormHelp,
  Heading,
  Label,
  LabeledControl,
  Legend,
  Modal,
  Paragraph,
  Radio,
  Text,
} from '@zillow/constellation-10';
import { useState } from 'react';
import MetroidIframeWrapper from '../../components/MetroidIframeWrapper';
import { BillingAddPaymentModalContainer } from '../../components/modals/BillingAddPaymentModal';
import { manifest } from '../../app/routes';
import { Redirect, useHistory } from 'react-router-dom';
// We'll create our own summary components inline since we removed the styled ones
import { useAppGuestPay } from '../../hooks/useGuestPay';
import { PaymentType } from '../../model/PaymentType';
import type { AdproductsGuestPayZuoraDto } from '@zg-rentals/ts-adproducts-client';
import { AdproductsGuestBillingInvoiceApiHandler_updateGuestPayPaymentMethodDetails } from '@zg-rentals/ts-adproducts-client';
import Cookies from 'js-cookie';
import {
  emitGuestPayPaymentMethodBackEvent,
  emitGuestPayPaymentMethodContinueEvent,
  emitGuestPayPaymentMethodSelectACHEvent,
  emitGuestPayPaymentMethodSelectCreditCardEvent,
} from '../../model/GuestPayClickstreamEvents';

const CREDIT_CARD_INFO_SUBTITLE = `Credit card payments should process almost immediately. 
  ACH payments can take five to eight business days to process — we’ll contact you if there are any issues.`;

export const DetailsPageWrapperConfig = {
  backgroundColor: 'gray.300',
  borderColor: 'white',
};

export const DetailsPageWrapperConfigContent = {
  backgroundColor: 'white' as const,
  borderRadius: '0px',
  borderColor: 'white' as const,
};

export const StyledPaymentFormContainer = ({ children }: { children: React.ReactNode }) => (
  <Box
    css={{
      width: '60%',
      padding: 'md',
      backgroundColor: 'white',
      border: '1px solid',
      borderColor: 'gray.300',
    }}
  >
    {children}
  </Box>
);

export const StyledSummaryContainer = ({ children }: { children: React.ReactNode }) => (
  <Box
    css={{
      width: '30%',
      border: '1px solid',
      borderColor: 'gray.300',
      padding: 'sm',
      backgroundColor: 'white',
      textAlign: 'center',
    }}
  >
    {children}
  </Box>
);

interface SummaryRowProps {
  borderTop?: boolean;
  borderBottom?: boolean;
  children: React.ReactNode;
}

const SummaryRow = ({ borderTop, borderBottom, children }: SummaryRowProps) => (
  <Box
    css={{
      display: 'flex',
      justifyContent: 'space-between',
      paddingBlock: 'xs',
      borderTop: borderTop ? '1px solid' : undefined,
      borderBottom: borderBottom ? '1px solid' : undefined,
      borderColor: 'gray.200',
    }}
  >
    {children}
  </Box>
);

interface InvoiceSummaryAmountListProps {
  invoices: Array<AdproductsGuestPayZuoraDto>;
}

export const InvoiceSummaryAmountList = ({ invoices }: InvoiceSummaryAmountListProps) => {
  const selectedInvoices = invoices.filter((inv) => inv.selectedForPayment);
  const balanceTotal = selectedInvoices.map((inv) => inv.balance).reduce((prev, cur) => prev + cur, 0);
  const originalTotal = selectedInvoices.map((inv) => inv.amountDue).reduce((prev, cur) => prev + cur, 0);
  const percentageOwed = balanceTotal / originalTotal;
  const taxTotal = selectedInvoices.map((inv) => inv.invoiceTaxAmount).reduce((prev, cur) => prev + cur, 0);
  const singleInvoice = selectedInvoices.length === 1 ? selectedInvoices[0] : null;
  const pluralChar = singleInvoice ? '' : 's';

  const formatCurrency = (amount: number) => {
    const amountString = numberUtils.asCurrency(amount);
    return amountString.endsWith('.00') ? amountString.slice(0, -3) : amountString;
  };

  const formatDate = ({ day, month, year }: LocalDate) => `${month}/${day}/${year}`;

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'left' }}>
      <Box css={{ marginBottom: 'md' }}>
        <Heading level={6}>{`Selected invoice${pluralChar} & amount${pluralChar}`}</Heading>
      </Box>
      <div>
        {singleInvoice ? (
          <>
            {singleInvoice.invoiceAddress?.street && (
              <SummaryRow>
                <Box>Property address</Box>
                <Paragraph css={{ textAlign: 'right' }}>{singleInvoice.invoiceAddress.street}</Paragraph>
              </SummaryRow>
            )}
            <SummaryRow>
              <Box>Invoice number</Box>
              <Paragraph css={{ textAlign: 'right' }}>{singleInvoice.invoiceNumber}</Paragraph>
            </SummaryRow>
            <SummaryRow>
              <Box>Due date</Box>
              <Paragraph css={{ textAlign: 'right' }}>{formatDate(singleInvoice.dueDate)}</Paragraph>
            </SummaryRow>
            <SummaryRow>
              <Box>Service period</Box>
              <Paragraph css={{ textAlign: 'right' }}>
                {formatDate(singleInvoice.serviceStart) + '-' + formatDate(singleInvoice.serviceEnd)}
              </Paragraph>
            </SummaryRow>
          </>
        ) : (
          selectedInvoices.map((invoice) => (
            <SummaryRow key={invoice.invoiceId}>
              <Box>
                {Cookies.get(BETA_COOKIE) === 'true' ? `Invoice ${invoice.invoiceNumber}` : invoice.invoiceNumber}
              </Box>
              <Box css={{ textAlign: 'right' }}>{formatCurrency(invoice.balance)}</Box>
            </SummaryRow>
          ))
        )}
        {Cookies.get(BETA_COOKIE) === 'true' ? (
          <>
            <SummaryRow borderBottom={true} borderTop={true}>
              <Box>
                <Text textStyle="body-bold">Total</Text>
              </Box>
              <Box css={{ textAlign: 'right' }}>
                <Text textStyle="body-bold">{formatCurrency(balanceTotal)}</Text>
                {taxTotal > 0 && <Text>{` (includes ${formatCurrency(taxTotal * percentageOwed)} tax)`}</Text>}
              </Box>
            </SummaryRow>
          </>
        ) : (
          <SummaryRow borderBottom={true} borderTop={true}>
            <Box>
              <Text textStyle="body-bold">Total</Text>
            </Box>
            <Box css={{ textAlign: 'right' }}>
              <Text textStyle="body-bold">{formatCurrency(balanceTotal)}</Text>
            </Box>
          </SummaryRow>
        )}
      </div>
    </Box>
  );
};

export const GuestPayPaymentDetailsPage = () => {
  const [paymentChoice, setPaymentChoice] = useState<PaymentType>(PaymentType.ACH);
  const [paymentInfoSubmitted, setPaymentInfoSubmitted] = useState(false);
  const [failedSubmit, setFailedSubmit] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);

  const history = useHistory();
  const { guestPayState, updateGuestPayState } = useAppGuestPay();
  const accountNumber = guestPayState.accountNumber;
  const { user } = useRentalManagerUser();

  const modalIsOpen = submitClicked && !paymentInfoSubmitted;

  const renderIframe = () => (
    <BillingAddPaymentModalContainer>
      <MetroidIframeWrapper
        onCreditCardAdded={handlePaymentMethodAdded}
        accountNumber={accountNumber || ''}
        selectedType={paymentChoice}
        guestPaySessionId={guestPayState.guestPaySessionKey}
      />
    </BillingAddPaymentModalContainer>
  );

  const onPaymentModalClose = () => {
    if (!paymentInfoSubmitted) {
      setFailedSubmit(true);
      setSubmitClicked(false);
    } else {
      history.push(manifest.guestPaySubmitPaymentPage);
    }
  };

  const renderForm = () => {
    return (
      <FieldSet
        legend={<Legend size="lg">Payment method</Legend>}
        error={failedSubmit}
        description={
          <FormHelp>
            {failedSubmit ? 'Your payment method was not successfully submitted. Please try again.' : ''}
          </FormHelp>
        }
      >
        <LabeledControl
          label={<Label>Pay by bank withdrawal (ACH)</Label>}
          control={<Radio name="FormField-radio" defaultChecked />}
          onChange={() => {
            emitGuestPayPaymentMethodSelectACHEvent({ user });
            setPaymentChoice(PaymentType.ACH);
          }}
        />
        <LabeledControl
          label={<Label>Pay by credit card</Label>}
          control={<Radio name="FormField-radio" />}
          onChange={() => {
            emitGuestPayPaymentMethodSelectCreditCardEvent({ user });
            setPaymentChoice(PaymentType.CREDIT_CARD);
          }}
        />
      </FieldSet>
    );
  };

  const handlePaymentMethodAdded = (result: MetroidPaymentMethodResultType) => {
    AdproductsGuestBillingInvoiceApiHandler_updateGuestPayPaymentMethodDetails({
      guestPaymentSessionId: guestPayState.guestPaySessionKey,
      paymentMethodId: result.payment_method_id,
    })
      .then(() => {
        updateGuestPayState({ paymentMethodId: result.payment_method_id, paymentType: paymentChoice });
        setPaymentInfoSubmitted(true);
        return history.push(manifest.guestPaySubmitPaymentPage);
      })
      .catch(() => setPaymentInfoSubmitted(false));
  };

  const PaymentModal = () => (
    <Modal
      open={modalIsOpen}
      onOpenChange={(open) => {
        if (!open) onPaymentModalClose();
      }}
      size="lg"
      header="Add a new payment method"
      body={<BillingAddPaymentModalContainer>{renderIframe()}</BillingAddPaymentModalContainer>}
    />
  );

  const handleNext = () => {
    emitGuestPayPaymentMethodContinueEvent({ user });
    setSubmitClicked(true);
  };

  const handleBack = () => {
    emitGuestPayPaymentMethodBackEvent({ user });
    return history.push(manifest.guestPayInvoicesPage);
  };

  if (!guestPayState.invoices.length) {
    return <Redirect to={manifest.guestPayPage} />;
  }

  return (
    <>
      <PageWrapper
        title="Enter your payment details"
        subTitle={CREDIT_CARD_INFO_SUBTITLE}
        config={DetailsPageWrapperConfig}
        configContent={DetailsPageWrapperConfigContent}
      >
        <div>
          <Box>
            <PaymentModal />
            <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <StyledPaymentFormContainer>{renderForm()}</StyledPaymentFormContainer>
              <StyledSummaryContainer>
                <InvoiceSummaryAmountList invoices={guestPayState.invoices} />
              </StyledSummaryContainer>
            </Box>
          </Box>
        </div>
        <Box css={{ marginBlock: 'sm' }}>
          <GuestPayFooter handleBackButton={handleBack} handleNext={handleNext} />
        </Box>
      </PageWrapper>
    </>
  );
};
